"use client";

// ================================================================
// BabyOdin 活动账号权限检查Hook
// 版本: 1.0
// 创建日期: 2025-09-02
// ================================================================

import { useCallback, useEffect, useState } from "react";
import { useIdentity } from "@/contexts/IdentityContext";
import {
	type ActivityAccountStatus,
	type ActivityAccountType,
	type ActivityFeature,
	hasFeaturePermission,
} from "@/types/activity-account";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("hooks/useActivityAccount.ts");

/**
 * useActivityAccount Hook返回值类型
 */
interface UseActivityAccountResult extends ActivityAccountStatus {
	/** 是否正在加载 */
	isLoading: boolean;
	/** 错误信息 */
	error: string | null;
	/** 刷新权限信息 */
	refresh: () => Promise<void>;
	/** 检查是否有指定功能权限 */
	hasPermission: (feature: ActivityFeature) => boolean;
}

/**
 * 活动账号权限管理Hook
 *
 * 功能：
 * - 检查用户是否有活动账号或超级账号权限
 * - 提供权限刷新功能
 * - 提供功能权限检查方法
 * - 自动缓存权限信息（5分钟）
 */
export function useActivityAccount(): UseActivityAccountResult {
	const { principal, isAuthenticated } = useIdentity();

	const [status, setStatus] = useState<ActivityAccountStatus>({
		hasActivityAccount: false,
		hasSuperAccount: false,
		accountType: "NONE",
	});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * 获取用户活动账号权限信息
	 */
	const fetchPermissions = useCallback(
		async (useCache: boolean = true): Promise<void> => {
			if (!isAuthenticated || !principal) {
				setStatus({
					hasActivityAccount: false,
					hasSuperAccount: false,
					accountType: "NONE",
				});
				setError(null);
				return;
			}

			// 检查缓存
			const cacheKey = `activity_account_${principal}`;
			const cacheExpireKey = `${cacheKey}_expire`;

			if (useCache) {
				const cached = localStorage.getItem(cacheKey);
				const expireTime = localStorage.getItem(cacheExpireKey);

				if (cached && expireTime && Date.now() < parseInt(expireTime)) {
					try {
						const cachedStatus = JSON.parse(cached) as ActivityAccountStatus;
						if (cachedStatus.grantedAt) {
							cachedStatus.grantedAt = new Date(cachedStatus.grantedAt);
						}
						setStatus(cachedStatus);
						setError(null);
						return;
					} catch (err) {
						// 缓存数据损坏，继续获取最新数据
						uiLog.warn("缓存数据解析失败:", err);
					}
				}
			}

			setIsLoading(true);
			setError(null);

			try {
				const response = await fetch(`/api/activity-accounts/${principal}`, {
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"Cache-Control": "no-cache",
					},
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const data = await response.json();

				if (data.success) {
					const newStatus: ActivityAccountStatus = {
						hasActivityAccount: false,
						hasSuperAccount: false,
						accountType: "NONE",
					};

					if (data.permission) {
						const { account_type, granted_by, granted_at, granted_reason } =
							data.permission;

						newStatus.accountType = account_type as ActivityAccountType;
						newStatus.hasActivityAccount =
							account_type === "ACTIVITY" || account_type === "SUPER";
						newStatus.hasSuperAccount = account_type === "SUPER";
						newStatus.grantedBy = granted_by;
						newStatus.grantedAt = new Date(granted_at);
						newStatus.grantedReason = granted_reason;
					}

					setStatus(newStatus);

					// 缓存权限信息（5分钟）
					const expireTime = Date.now() + 5 * 60 * 1000;
					localStorage.setItem(cacheKey, JSON.stringify(newStatus));
					localStorage.setItem(cacheExpireKey, expireTime.toString());

					uiLog.info("活动账号权限更新:", newStatus);
				} else {
					throw new Error(data.error || "获取权限信息失败");
				}
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : "网络错误";
				uiLog.error("获取活动账号权限失败:", err);
				setError(errorMessage);

				// 失败时设置为无权限状态
				setStatus({
					hasActivityAccount: false,
					hasSuperAccount: false,
					accountType: "NONE",
				});
			} finally {
				setIsLoading(false);
			}
		},
		[isAuthenticated, principal],
	);

	/**
	 * 刷新权限信息（跳过缓存）
	 */
	const refresh = useCallback(async (): Promise<void> => {
		return fetchPermissions(false);
	}, [fetchPermissions]);

	/**
	 * 检查是否有指定功能的权限
	 */
	const hasPermission = useCallback(
		(feature: ActivityFeature): boolean => {
			return hasFeaturePermission(feature, status.accountType);
		},
		[status.accountType],
	);

	/**
	 * 清除缓存
	 */
	const clearCache = useCallback(() => {
		if (principal) {
			const cacheKey = `activity_account_${principal}`;
			const cacheExpireKey = `${cacheKey}_expire`;
			localStorage.removeItem(cacheKey);
			localStorage.removeItem(cacheExpireKey);
		}
	}, [principal]);

	// 监听认证状态变化
	useEffect(() => {
		if (isAuthenticated && principal) {
			// 登录后自动获取权限信息
			fetchPermissions(true);
		} else {
			// 未登录时清空状态和缓存
			setStatus({
				hasActivityAccount: false,
				hasSuperAccount: false,
				accountType: "NONE",
			});
			setError(null);
			clearCache();
		}
	}, [isAuthenticated, principal, fetchPermissions, clearCache]);

	return {
		...status,
		isLoading,
		error,
		refresh,
		hasPermission,
	};
}

/**
 * 活动账号权限检查Hook（轻量版）
 * 仅返回基本的权限状态，不包含详细信息
 */
export function useActivityAccountStatus() {
	const { hasActivityAccount, hasSuperAccount, accountType, isLoading } =
		useActivityAccount();

	return {
		hasActivityAccount,
		hasSuperAccount,
		accountType,
		isLoading,
	};
}

/**
 * 超级账号权限检查Hook
 * 专门用于需要超级账号权限的组件
 */
export function useSuperAccountOnly() {
	const { hasSuperAccount, isLoading, error, refresh } = useActivityAccount();

	return {
		hasSuperAccount,
		isLoading,
		error,
		refresh,
		/** 是否有访问权限 */
		hasAccess: hasSuperAccount,
	};
}

/**
 * 活动账号权限检查Hook（包含超级账号）
 * 专门用于需要活动账号或超级账号权限的组件
 */
export function useActivityAccountOnly() {
	const { hasActivityAccount, accountType, isLoading, error, refresh } =
		useActivityAccount();

	return {
		hasActivityAccount,
		accountType,
		isLoading,
		error,
		refresh,
		/** 是否有访问权限 */
		hasAccess: hasActivityAccount,
	};
}
